import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ReactComponent as LogoFPCH } from '../ui/logoFPCH.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NavbarHamburger from '../ui/NavbarHamburger';
import UserCircle from '../ui/UserCircle';
import useAuth from '../hooks/useAuth';
interface NavbarProps {
  descriptionVisible: boolean;
  simplified?: boolean;
  hideLoginPrompt?: boolean;
}

function Navbar({ descriptionVisible, simplified = false, hideLoginPrompt = false }: NavbarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { auth } = useAuth();
  const [isOpenLoggedMenu, setIsOpenLoggedMenu] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(window.matchMedia('(min-width: 1024px)').matches);

  // Track desktop breakpoint
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Helper function to determine if bubble should be shown
  const showBubble = (path: string) => {
    const isActive = location.pathname === path;
    return hoveredLink ? hoveredLink === path : isActive;
  };

  // Redirect to login page when clicking on login button
  const handleClickLogin = () => {
    if (!auth.isAuth) {
      navigate('/login');
      setIsOpen(false);
    } else {
      setIsOpenLoggedMenu(!isOpenLoggedMenu);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!auth.isAuth) {
      setIsOpenLoggedMenu(false);
    }
  }, [auth]);

  // Reset menu and body overflow on route change
  useEffect(() => {
    setIsOpen(false);
    setIsOpenLoggedMenu(false);
    document.body.style.overflow = 'unset';
  }, [location.pathname]);

  // Clean up overflow when component unmounts or menu closes
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Toggle the navbar menu on mobile
  const toggleMenu = () => {
    if (isOpenLoggedMenu || isOpen) {
      setIsOpenLoggedMenu(false);
      setIsOpen(false);
      // Re-enable body scroll
      document.body.style.overflow = 'unset';
    } else {
      setIsOpen(true);
      // Disable body scroll
      document.body.style.overflow = 'hidden';
    }
  };

  if (!simplified && !descriptionVisible && !hideLoginPrompt) {
    return (
      <header className="w-full h-fit flex flex-col">
        <div className="bg-fpcwhite border-t-4 border-fpcred w-full h-fit justify-center flex">
          <nav className="max-w-screen-xl w-full flex items-center justify-center px-4 py-4 relative">
            <a href="/" className="absolute left-4">
              <img src="/logoFPC192.png" alt="FPC" className="h-10 w-auto md:hidden" />
              <LogoFPCH className="h-10 w-auto hidden md:block" viewBox="0 0 1908 562" />
            </a>
            <div className="flex gap-3 sm:gap-4 items-center justify-center mt-4">
              <p className="text-xl font-semibold text-gray-600">Hai già un account?</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 sm:px-8 sm:py-3 lg:px-10 lg:py-4 rounded-xl bg-fpcred text-white font-semibold hover:bg-buttonhover transition-all duration-200 transform hover:scale-105 shadow-lg text-xl">
                Accedi
              </button>
            </div>
          </nav>
        </div>
      </header>
    );
  }

  return (
    <>
      {/*The header component sets the width of the navbar and draw he top red line.*/}
      <header className={`w-full h-fit flex flex-col`}>
        <div className={`bg-fpcwhite border-t-4  border-fpcred w-full h-fit justify-center flex`}>
          <nav className="max-w-screen-xl w-full flex flex-col items-center lg:flex-row">
            {/* This div is for align in a single line the logo and the button on mobile */}
            <div className="bg-fpcwhite flex w-full lg:w-auto justify-between items-center px-4 py-4 relative z-[9999]">
              <a href="/" className="flex-shrink-0 opacity-100 relative z-[9999] block">
                {/* Versione combinata per mobile */}
                <div className="md:hidden flex items-center space-x-3">
                  <img src="/logoFPC192.png" alt="FPC" className="h-10 w-auto" />
                  <div className="flex flex-col text-sm leading-tight font-sans">
                    <div className="text-black">
                      <span className="text-fpcred font-bold">F</span>ORMANDO
                    </div>
                    <div className="text-black">
                      <span className="text-fpcred font-bold">P</span>ER<span className="text-fpcred font-bold">C</span>
                      ORSI
                    </div>
                  </div>
                </div>
                {/* Versione SVG completa per desktop */}
                <LogoFPCH className="h-10 w-auto pr-1 hidden md:block" viewBox="0 0 1908 562" />
              </a>
              {!simplified && !hideLoginPrompt && (
                <div className="flex flex-row w-fit h-fit space-x-5 items-center">
                  {!auth.isAuth && (
                    <button
                      onClick={() => navigate('/login')}
                      className="lg:hidden px-4 py-1.5 rounded-full bg-fpcred text-white font-semibold text-sm hover:bg-buttonhover transition-all duration-200">
                      Accedi
                    </button>
                  )}
                  <button className="flex lg:hidden" onClick={toggleMenu}>
                    <NavbarHamburger isOpen={isOpen || isOpenLoggedMenu} />
                  </button>
                </div>
              )}
            </div>
            {/* Navigation links container */}
            {descriptionVisible && (
              <motion.div
                className="bg-fpcwhite w-full space-y-6 lg:space-y-0 items-center flex-col flex md:flex-1 mx-auto max-lg:fixed left-0 right-0 top-[4.5rem] z-[9999] lg:relative lg:top-auto lg:left-auto lg:right-auto lg:flex-row lg:h-fit lg:pl-0 lg:pt-1 lg:pb-1 lg:z-auto overflow-hidden lg:overflow-visible"
                initial={false}
                animate={{
                  height: isDesktop ? 'auto' : isOpen ? 'calc(100vh - 4.5rem)' : 0,
                  paddingTop: isDesktop ? undefined : isOpen ? 32 : 0,
                  paddingBottom: isDesktop ? undefined : isOpen ? 32 : 0,
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                }}>
                <ul className="lg:flex lg:space-x-8 flex flex-col space-y-6 lg:space-y-0 lg:flex-row lg:bg-fpcpink px-4 py-2 rounded-md lg:mx-auto">
                  <li
                    className="w-fit relative group"
                    onMouseEnter={() => setHoveredLink('/')}
                    onMouseLeave={() => setHoveredLink(null)}>
                    <NavLink
                      to="/"
                      onClick={toggleMenu}
                      className={`font-sans text-2xl lg:text-base transition-all duration-500 ease-out relative z-10 px-3 py-1 block ${
                        showBubble('/') ? 'text-white' : location.pathname === '/' ? 'text-fpcred' : 'text-grey1'
                      }`}>
                      {t('homeTitle')}
                      <span
                        className={`absolute inset-0 bg-fpcred rounded-full transition-transform duration-500 ease-out origin-bottom -z-10 ${
                          showBubble('/') ? 'scale-y-100' : 'scale-y-0'
                        }`}></span>
                    </NavLink>
                  </li>
                  <li
                    className="w-fit relative group"
                    onMouseEnter={() => setHoveredLink('/chi-siamo')}
                    onMouseLeave={() => setHoveredLink(null)}>
                    <NavLink
                      to="/chi-siamo"
                      onClick={toggleMenu}
                      className={`font-sans text-2xl lg:text-base transition-all duration-500 ease-out relative z-10 px-3 py-1 block ${
                        showBubble('/chi-siamo')
                          ? 'text-white'
                          : location.pathname === '/chi-siamo'
                          ? 'text-fpcred'
                          : 'text-grey1'
                      }`}>
                      {t('aboutTitle')}
                      <span
                        className={`absolute inset-0 bg-fpcred rounded-full transition-transform duration-500 ease-out origin-bottom -z-10 ${
                          showBubble('/chi-siamo') ? 'scale-y-100' : 'scale-y-0'
                        }`}></span>
                    </NavLink>
                  </li>
                  <li
                    className="w-fit relative group"
                    onMouseEnter={() => setHoveredLink('/prezzi')}
                    onMouseLeave={() => setHoveredLink(null)}>
                    <button
                      onClick={() => {
                        if (isOpen || isOpenLoggedMenu) {
                          setIsOpen(false);
                          setIsOpenLoggedMenu(false);
                          document.body.style.overflow = 'unset';
                        }
                        if (location.pathname === '/') {
                          document.getElementById('prezzi')?.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          navigate('/#prezzi');
                        }
                      }}
                      className={`font-sans text-2xl lg:text-base transition-all duration-500 ease-out relative z-10 px-3 py-1 block ${
                        hoveredLink === '/prezzi' ? 'text-white' : 'text-grey1'
                      }`}>
                      {t('pricingTitle')}
                      <span
                        className={`absolute inset-0 bg-fpcred rounded-full transition-transform duration-500 ease-out origin-bottom -z-10 ${
                          showBubble('/prezzi') ? 'scale-y-100' : 'scale-y-0'
                        }`}></span>
                    </button>
                  </li>
                  <li
                    className="w-fit relative group"
                    onMouseEnter={() => setHoveredLink('/lavora-con-noi')}
                    onMouseLeave={() => setHoveredLink(null)}>
                    <NavLink
                      to="/lavora-con-noi"
                      onClick={toggleMenu}
                      className={`font-sans text-2xl lg:text-base transition-all duration-500 ease-out relative z-10 px-3 py-1 block ${
                        showBubble('/lavora-con-noi')
                          ? 'text-white'
                          : location.pathname === '/lavora-con-noi'
                          ? 'text-fpcred'
                          : 'text-grey1'
                      }`}>
                      {t('workWithUsTitle')}
                      <span
                        className={`absolute inset-0 bg-fpcred rounded-full transition-transform duration-500 ease-out origin-bottom -z-10 ${
                          showBubble('/lavora-con-noi') ? 'scale-y-100' : 'scale-y-0'
                        }`}></span>
                    </NavLink>
                  </li>
                </ul>
              </motion.div>
            )}
            {/* Auth button container */}
            {descriptionVisible && (
              <motion.div
                className="bg-fpcwhite max-lg:fixed left-0 right-0 top-[28rem] z-[9999] lg:relative lg:top-auto lg:z-auto flex justify-center lg:pr-10"
                initial={false}
                animate={{
                  opacity: isDesktop ? 1 : isOpen ? 1 : 0,
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                }}
                style={{ pointerEvents: isDesktop || isOpen ? 'auto' : 'none' }}>
                <button
                  className={`font-sans text-2xl lg:text-base transition-transform duration-300 py-2 lg:py-1 ease-in-out text-white hover:scale-110 ${
                    auth.isAuth ? 'bg-inherit w-fit h-fit px-1' : 'bg-fpcred rounded-full px-8 lg:px-6 max-lg:hidden'
                  }`}
                  onClick={handleClickLogin}>
                  {auth.isAuth ? (
                    <UserCircle isOpen={isOpenLoggedMenu} mouseHover={'stroke-black'} openPar={'stroke-fpcred'} />
                  ) : (
                    t('login.title')
                  )}
                </button>
              </motion.div>
            )}
          </nav>
        </div>
      </header>
    </>
  );
}
export default Navbar;
